import { useOptimistic } from "@app/shared/utils/optimisticUpdates";
import {
    AnswerStatus,
    ValidationStatus,
    type CreateFileInput,
} from "@generated/client/graphql";
import { useMemo, useState, type ChangeEvent } from "react";
import {
    useUpdateAnswerStatus,
    useUpsertSupplierQuestionnaireQuestionAnswer,
} from "../../data";
import { answerOptimisticUpdateFn, isProofMissing } from "../../services";
import {
    type AnswerInput,
    type AnswerValue,
    type Question as TQuestion,
} from "../../types";

export const useAnswer = (
    question: TQuestion,
    { isOpen, questionId }: { isOpen?: boolean; questionId: string },
) => {
    const answer = question.answer;
    const [optimisticAnswer, setOptimisticAnswer] = useOptimistic(answer);

    const [isCommentSectionOpen, setIsCommentSectionOpen] = useState(
        !!answer?.comment,
    );

    const [isValidated, setIsValidated] = useState<boolean>(
        answer?.status === AnswerStatus.Validated,
    );

    const textAnswer: string | undefined = useMemo(() => {
        if (optimisticAnswer?.value?.text) {
            return optimisticAnswer?.value?.text;
        }
        if (optimisticAnswer?.value?.num) {
            return String(optimisticAnswer?.value?.num);
        }
        if (optimisticAnswer?.value?.options) {
            return optimisticAnswer?.value?.options.join(", ");
        }
        return undefined;
    }, [optimisticAnswer]);

    const {
        mutate,
        mutateAsync,
        isPending: isUpsertLoading,
    } = useUpsertSupplierQuestionnaireQuestionAnswer(questionId);

    const saveAnswer = (input: AnswerInput) => {
        mutate({
            id: optimisticAnswer?.id,
            ...input,
            questionId,
        });
    };

    const answerStatus = useUpdateAnswerStatus(questionId);

    const handleUploadDocuments = (files?: CreateFileInput[]) => {
        if (!files?.length) return;
        return mutateAsync({
            id: optimisticAnswer?.id,
            questionId,
            createAndConnectEvidenceFiles: files,
        });
    };

    const handleRemoveDocument = (fileId: string) => {
        return mutate({
            id: optimisticAnswer?.id,
            questionId,
            disconnectEvidenceFileIds: [fileId],
        });
    };

    const makeAnswerHandler =
        <
            E extends
                | ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
                | number[]
                | number
                | boolean,
        >(
            onChange: (evt: E) => { value: AnswerValue } | { comment: string },
            /**
             * If true, the answer will be send to the backend
             */
            save: boolean = false,
        ) =>
        (evt: E) => {
            if (!isOpen) return;

            const input = onChange(evt);
            setOptimisticAnswer((prev) =>
                answerOptimisticUpdateFn(
                    { prevAnswer: prev, questionId },
                    input,
                ),
            );
            if (save) {
                saveAnswer(input);
            }
        };

    const disableDataInputs =
        !isOpen ||
        !!optimisticAnswer?.value?.na ||
        optimisticAnswer?.status === AnswerStatus.Validated;

    const showMissingCommentWarning = useMemo(() => {
        if (optimisticAnswer?.value?.na) {
            return !optimisticAnswer?.comment;
        }
        return false;
    }, [optimisticAnswer]);

    const canBeValidated = useMemo(() => {
        const hasValidAnswer =
            (textAnswer != null && textAnswer.trim() !== "") ||
            (optimisticAnswer?.value?.na && optimisticAnswer?.comment?.trim());
        const hasRequiredEvidence = !isProofMissing(question);

        return isOpen && hasValidAnswer && hasRequiredEvidence && !isValidated;
    }, [textAnswer, optimisticAnswer, question, isValidated]);

    const canBeReset = useMemo(() => {
        return (
            isOpen &&
            !isValidated &&
            (textAnswer ||
                optimisticAnswer?.value?.na ||
                (optimisticAnswer?.comment != null &&
                    optimisticAnswer?.comment.trim() !== "") ||
                (optimisticAnswer?.evidenceFiles?.length ?? 0) > 0)
        );
    }, [textAnswer, optimisticAnswer, isValidated]);

    const handleNoteButtonClick = () => {
        setIsCommentSectionOpen(true);
    };

    const handleDeleteCommentClick = () => {
        setIsCommentSectionOpen(false);
        saveAnswer({
            comment: "",
        });
    };

    const validateAnswer = (status: ValidationStatus) => {
        saveAnswer({
            status,
        });
    };

    const handleValidationClick = () => {
        if (!canBeValidated) return;

        setIsValidated(true);
        validateAnswer(ValidationStatus.Validated);
    };

    const handleResetValidationClick = () => {
        if (!isValidated) return;
        setIsValidated(false);
        validateAnswer(ValidationStatus.Notvalidated);
    };

    const handleResetClick = () => {
        if (!canBeReset) return;
        setIsCommentSectionOpen(false);
        mutate({
            id: optimisticAnswer?.id,
            questionId,
            value: {
                text: null,
                num: null,
                options: [],
                na: null,
            },
            comment: null,
            disconnectEvidenceFileIds: optimisticAnswer?.evidenceFiles
                ?.map((file) => file.id)
                .filter(Boolean),
        });
    };

    const handleNoDataSwitchChange = (checked: boolean) => {
        if (checked) {
            setIsCommentSectionOpen(true);
        }
        const handler = makeAnswerHandler(
            (evt: boolean) => ({
                value: { na: evt },
            }),
            true,
        );
        handler(checked);
    };

    return {
        textAnswer,
        disableDataInputs,
        handleCommentChange: makeAnswerHandler(
            (evt: ChangeEvent<HTMLTextAreaElement>) => ({
                comment: evt.target.value,
            }),
        ),

        handleValueChange: makeAnswerHandler(
            (evt: ChangeEvent<HTMLInputElement>) => {
                const num =
                    evt.target.value === "" || isNaN(Number(evt.target.value))
                        ? null
                        : Number(evt.target.value);
                return {
                    value: { num },
                };
            },
        ),
        handleTextChange: makeAnswerHandler(
            (evt: ChangeEvent<HTMLTextAreaElement>) => ({
                value: { text: evt.target.value },
            }),
        ),
        handleSingleSelectChange: makeAnswerHandler(
            (evt: number) => ({
                value: { options: [evt] },
            }),
            true,
        ),

        handleMultiSelectChange: makeAnswerHandler(
            (evt: number[]) => ({
                value: { options: evt },
            }),
            true,
        ),

        handleCommentBlur: () => {
            saveAnswer({
                comment: optimisticAnswer?.comment ?? "",
            });
        },

        handleTextOrValueBlur: () => {
            saveAnswer({
                value: optimisticAnswer?.value ?? {},
            });
        },
        handleUploadDocuments,
        handleRemoveDocument,
        isUpsertLoading,
        optimisticAnswer,
        showMissingCommentWarning,
        answerStatus,
        isCommentSectionOpen,
        setIsCommentSectionOpen,
        isValidated,
        canBeValidated,
        canBeReset,
        handleNoteButtonClick,
        handleDeleteCommentClick,
        handleNoDataSwitchChange,
        handleValidationClick,
        handleResetValidationClick,
        handleResetClick,
    };
};
